/**
 * TodaysLesson — student-facing view of today's flight-plan lesson.
 *
 * Same lesson the teacher sees on `/teacher-dashboard`, plus:
 *   • Daily Reading Room — a real public-domain / open-license reading
 *     source (Project Gutenberg, NASA, Smithsonian, Illustrative Math…)
 *     anchored to today's CA standard.
 *   • "I've read this" tracker — the scholar taps once, we store a per-day
 *     marker so the teacher dashboard shows completion at a glance.
 */
import React, { useEffect, useState } from 'react';
import { BookOpen, ExternalLink, Loader2, Sparkles, Target, Check, Clock } from 'lucide-react';
import api from '../services/api';
import CoachBSpeak from './CoachBSpeak';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const TodaysLesson = () => {
  const [data, setData] = useState(null);
  const [unified, setUnified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/school/todays-lesson', { headers: authHeaders() }).then(r => r.data).catch(() => null),
      api.get('/school/today/unified-lesson?view=student', { headers: authHeaders() }).then(r => r.data).catch(() => null),
    ]).then(([base, uni]) => {
      setData(base);
      // Only set unified if this scholar's grade is in 5-8 AND the plan is present
      if (uni?.view === 'student' && uni?.lesson) setUnified(uni.lesson);
    }).finally(() => setLoading(false));
  }, []);

  const markRead = async () => {
    setMarking(true);
    try {
      const r = await api.post('/school/todays-lesson/mark-read', {}, { headers: authHeaders() });
      setData((d) => ({ ...d, read_at: r.data.read_at }));
      toast.success('Nice work — Coach B has it logged.');
    } catch { toast.error('Could not save — try again.'); }
    finally { setMarking(false); }
  };

  if (loading) {
    return (
      <div className="mb-6 border-2 border-zinc-800 px-5 py-4 flex items-center gap-3" data-testid="todays-lesson-loading">
        <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
        <span className="text-xs text-zinc-500">Loading today's lesson…</span>
      </div>
    );
  }
  if (!data?.lesson) return null;
  const l = data.lesson;
  const reading = data.reading;
  const readAloud = [
    `${l.subject}. ${l.daily_theme}`,
    `Today's standard: ${l.standard_code}. ${l.standard_text}.`,
    (l.mini_lesson || '').replace(/\*\*/g, ''),
  ].filter(Boolean).join(' ');

  return (
    <div className="mb-6 border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/[0.06] to-transparent" data-testid="todays-lesson">
      <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-orange-500 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-black" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] font-black text-orange-500">Today's Lesson · Grade {data.grade}</div>
            <div className="text-white font-black text-base leading-tight break-words">{l.subject}</div>
          </div>
        </div>
        <CoachBSpeak text={readAloud.slice(0, 480)} label="Coach B reads this" variant="compact" accent="#FF5722" testId="todays-lesson-coach-b" />
      </div>

      <div className="px-5 py-5 space-y-4">
        {l.daily_theme && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3 h-3 text-orange-400" />
              <span className="text-[9px] uppercase tracking-widest font-black text-orange-400">Theme</span>
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed">{l.daily_theme}</p>
          </div>
        )}

        <div className="border-2 border-orange-500/30 bg-black/40 px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-3 h-3 text-orange-400" />
            <span className="text-[9px] uppercase tracking-widest font-black text-orange-400">CA Standard</span>
            <span className="font-mono text-[11px] font-black text-white">{l.standard_code || '—'}</span>
          </div>
          <p className="text-[13px] text-zinc-300 leading-snug">{l.standard_text || 'Standard details coming soon.'}</p>
        </div>

        {reading && (
          <div className="border-2 border-cyan-500/50 bg-cyan-500/5 px-4 py-3" data-testid="reading-room">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-3 h-3 text-cyan-300" />
              <span className="text-[9px] uppercase tracking-widest font-black text-cyan-300">Daily Reading Room</span>
              <span className="text-[10px] font-mono text-cyan-200/70">· {reading.read_minutes} min</span>
            </div>
            <div className="text-white font-black text-[15px] leading-tight break-words">{reading.title}</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">{reading.author} · <span className="italic">{reading.source}</span></div>
            {reading.sport_tie_in && (
              <p className="text-[12px] text-cyan-200/90 mt-2 leading-snug italic">"{reading.sport_tie_in}"</p>
            )}
            <a href={reading.url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 mt-2 text-[10px] font-black uppercase tracking-widest text-cyan-300 hover:text-white transition-colors"
               data-testid="reading-open-link">
              Open Reading <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {l.mini_lesson && (
          <div className="border-2 border-indigo-500 bg-indigo-500/10 px-4 py-4" data-testid="todays-lesson-mini">
            <div className="text-[9px] uppercase tracking-widest font-black text-indigo-300 mb-2">Read this first · Coach B's Mini-Lesson</div>
            <div className="text-[14px] text-zinc-100 leading-relaxed space-y-3">
              {l.mini_lesson.split(/\n\s*\n/).map((para, i) => {
                const parts = para.split('**');
                return (
                  <p key={i} className="leading-[1.7]">
                    {parts.map((chunk, j) =>
                      j % 2 === 1
                        ? <strong key={j} className="text-orange-300 font-black block mt-2 mb-1 uppercase tracking-wide text-[11px]">{chunk}</strong>
                        : <span key={j}>{chunk}</span>
                    )}
                  </p>
                );
              })}
            </div>
          </div>
        )}

        {(l.prompts || []).length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-2">Think It Through</div>
            <ol className="space-y-2 pl-1">
              {l.prompts.map((p, i) => (
                <li key={i} className="flex gap-2 text-[13px] text-zinc-200 leading-snug">
                  <span className="flex-shrink-0 w-5 h-5 border border-orange-500 text-orange-400 flex items-center justify-center text-[10px] font-black">{i + 1}</span>
                  <span className="min-w-0">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Unified cross-grade lesson — videos + practice + exit ticket for scholar's grade */}
        {unified && (
          <div className="border-2 border-emerald-500/50 bg-emerald-500/5 px-4 py-4" data-testid="unified-lesson-block">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3 h-3 text-emerald-300" />
              <span className="text-[9px] uppercase tracking-widest font-black text-emerald-300">Today's Lesson · Live-Class Materials</span>
            </div>
            {unified.topic && (
              <div className="text-white font-black text-[15px] leading-tight mb-3">{unified.topic}</div>
            )}
            {unified.concept && (
              <p className="text-[13px] text-zinc-200 leading-relaxed mb-3">{unified.concept}</p>
            )}

            {(unified.videos || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[9px] uppercase tracking-widest font-black text-emerald-300 mb-2">Watch These</div>
                <div className="space-y-3">
                  {unified.videos.map((v, i) => (
                    <div key={i} className="border border-emerald-500/30 bg-black/50 overflow-hidden" data-testid={`unified-video-${i}`}>
                      <div className="relative pb-[56.25%] h-0 bg-black">
                        <iframe
                          src={v.embed_url}
                          title={v.title}
                          className="absolute inset-0 w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="px-3 py-2">
                        <div className="text-[13px] font-black text-white leading-snug">{v.title}</div>
                        <div className="text-[10px] text-emerald-300 mt-0.5">{v.channel}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(unified.practice_problems || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[9px] uppercase tracking-widest font-black text-emerald-300 mb-2">Practice — Your Grade</div>
                <ol className="space-y-2 pl-1">
                  {unified.practice_problems.map((p, i) => (
                    <li key={i} className="flex gap-2 text-[13px] text-zinc-200 leading-snug">
                      <span className="flex-shrink-0 w-5 h-5 border border-emerald-400 text-emerald-300 flex items-center justify-center text-[10px] font-black">{i + 1}</span>
                      <span className="min-w-0">{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {unified.exit_ticket && (
              <div className="border-2 border-orange-500/50 bg-orange-500/5 px-3 py-2">
                <div className="text-[9px] uppercase tracking-widest font-black text-orange-400 mb-1">Exit Ticket</div>
                <p className="text-[13px] text-zinc-100 leading-snug">{unified.exit_ticket}</p>
              </div>
            )}
          </div>
        )}

        {/* Mark read tracker */}
        <div className="pt-3 border-t border-zinc-900 flex items-center gap-3 flex-wrap">
          {data.read_at ? (
            <div className="inline-flex items-center gap-2 border-2 border-emerald-500 bg-emerald-500/10 px-4 py-2 text-emerald-300 text-[11px] font-black uppercase tracking-widest" data-testid="mark-read-done">
              <Check className="w-4 h-4" /> Read at {new Date(data.read_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
            </div>
          ) : (
            <button onClick={markRead} disabled={marking}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-4 py-2 text-[11px] font-black uppercase tracking-widest transition-colors disabled:opacity-60"
              data-testid="mark-read-btn">
              {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} I've read this
            </button>
          )}
          <span className="text-[10px] uppercase tracking-widest font-black text-zinc-600 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Now open Today's Worksheet ↓
          </span>
        </div>
      </div>
    </div>
  );
};

export default TodaysLesson;
